import { packingListTotals } from '../../invoice/invoicePtcMerchandise';
import { resolveInvoiceIssueDate } from '../../invoice/invoiceIssueDate';
import { invoiceNumberCorrelativo } from '../../invoice/invoiceNumberUtils';
import {
  formatHtCertificadoNrLine,
  htCertificadoNrFromFormulario,
} from './htCertificadoNr';
import * as S from './htStaticContent';
import { addDaysToIsoDate } from './htDateUtils';

function safe(s) {
  return s == null ? '' : String(s);
}

function foldCountry(value) {
  return safe(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
}

/** Brasil y Brazil son el mismo país; el resto se compara por el nombre. */
function countryKey(value) {
  const key = foldCountry(value);
  if (key === 'brasil' || key === 'brazil') return 'brasil';
  return key;
}

function sameCountry(a, b) {
  const ka = countryKey(a);
  const kb = countryKey(b);
  return Boolean(ka) && ka === kb;
}

function englishCountryName(value) {
  const t = safe(value).trim();
  if (!t) return '';
  if (countryKey(t) === 'brasil') return 'BRAZIL';
  return t.toUpperCase();
}

/**
 * Lugar HT como Invoice 5019: `CIUDAD, PAÍS`.
 * Quita el país entre paréntesis del puerto y no lo vuelve a escribir.
 * Brasil queda como BRAZIL.
 */
export function formatHtPlaceLine(place, country) {
  let city = safe(place).replace(/\s+/g, ' ').trim();
  let paren = '';
  const wrapped = city.match(/^(.*?)\s*\(([^)]+)\)\s*(.*)$/);
  if (wrapped) {
    city = `${wrapped[1]} ${wrapped[3]}`.replace(/\s+/g, ' ').trim();
    paren = wrapped[2].trim();
  }

  const english = englishCountryName(country || paren);
  const cityName = city
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && !sameCountry(part, english) && !sameCountry(part, paren))
    .join(', ')
    .toUpperCase();

  if (!cityName) return english;
  return english ? `${cityName}, ${english}` : cityName;
}

function invoiceFieldsForHt(inv) {
  const f = inv?.datos_formulario && typeof inv.datos_formulario === 'object'
    ? inv.datos_formulario
    : {};
  return {
    clienteNombre: inv?.nombre_cliente || f.clienteNombre || '',
    clienteDireccion: f.clienteDireccion || '',
    puertoOrigen: f.puertoOrigen || '',
    paisOrigen: f.paisOrigen || '',
    puertoDestino: f.puertoDestino || '',
    paisDestino: inv?.pais_destino || f.paisDestino || '',
    lugarEntrega: f.lugarEntrega || '',
    nombreNave: f.nombreNave || '',
    nombreBl: inv?.nombre_bl || f.nombreBl || '',
  };
}

/**
 * @param {object} pl — GET /packinglists/:id
 * @param {object} inv — GET /commercialinvoices/:id
 */
export function buildHtParams(pl, inv) {
  const f = inv?.datos_formulario && typeof inv.datos_formulario === 'object'
    ? inv.datos_formulario
    : {};
  const ptc = invoiceFieldsForHt(inv);
  const totals = packingListTotals(pl?.lineas || []);
  const issueIso = resolveInvoiceIssueDate(inv, pl);
  const certificadoNr = htCertificadoNrFromFormulario(f);

  const consignee = [ptc.clienteNombre, ptc.clienteDireccion]
    .map((x) => safe(x).trim())
    .filter(Boolean)
    .join('\n');

  const origin = formatHtPlaceLine(ptc.puertoOrigen, ptc.paisOrigen);
  const destination = formatHtPlaceLine(ptc.puertoDestino || ptc.lugarEntrega, ptc.paisDestino);

  const totalM3 = f.totalM3 != null && f.totalM3 !== '' ? Number(f.totalM3) : totals.totalM3;
  const bl = safe(ptc.nombreBl || inv?.nombre_bl || pl?.nombre_bl).trim();

  return {
    numeroInvoice: invoiceNumberCorrelativo(inv?.numero_invoice || ''),
    certificadoNrLine: formatHtCertificadoNrLine(certificadoNr),
    quantityProducts: totals.totalBundles,
    quantityTreated: totalM3,
    consigneeAddress: consignee,
    origin,
    destination,
    treatmentDateIso: addDaysToIsoDate(issueIso, -25),
    certificateIssueDateIso: addDaysToIsoDate(issueIso, 3),
    remarkBlInvoice: bl ? `BL ${bl}` : '',
    grossWeight: f.grossWeight != null ? Number(f.grossWeight) : null,
    netWeight: f.netWeight != null ? Number(f.netWeight) : null,
    volumeDescription: totalM3,
    distinctiveMark:
      ptc.nombreNave && bl
        ? `Vessel:  ${safe(ptc.nombreNave).trim()} /${bl}`
        : ptc.nombreNave
          ? `Vessel:  ${safe(ptc.nombreNave).trim()}`
          : '',
    static: S,
  };
}

export function htFileBase(params) {
  const inv = safe(params?.numeroInvoice).replace(/\W/g, '_') || 'export';
  return `HT_${inv}`;
}
